import { useForm } from "@tellescope/forms";
import { Button, Dialog, DialogTrigger, RichTextEditor, Textarea } from "@tellescope/ui";
import { ReactNode, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, File, FileText, Paperclip } from "lucide-react";
import { TellescopeModelFromName, useAuth } from "@tellescope/tanstack-query";
import { formatContentInlineTemplateString } from "@tellescope/utils";
import { AttachFileDialog } from "../pages/messaging/components/conversation-panel/AttachFileDialog";
import { InboxAttachmentPreview } from "../pages/messaging/components/conversation-panel/InboxAttachmentPreview";
import { ScheduleMessageDialog } from "../pages/messaging/components/conversation-panel/ScheduleMessageDialog";
import { useMessagingAttachments } from "../hooks/useMessagingAttachments";
import type { ChatAttachment } from "../hooks/useMessagingAttachments";
import { i18n } from "@tellescope/i18n";
import { InboxTemplateSelectorDialogContents } from "./inbox";

export interface MessageCompositionFooterPayload {
  message: string;
  attachments: ChatAttachment[];
  files: TellescopeModelFromName<"files">[];
}

export type MessageCompositionFooterChannelType = "sms" | "chat" | "email" | "zendesk";

export interface MessageCompositionFooterProps {
  onSubmit: (payload: MessageCompositionFooterPayload) => void;
  onSchedule?: (payload: MessageCompositionFooterPayload, sendAt: Date) => void;
  onSendNow?: (payload: MessageCompositionFooterPayload) => void;
  onSaveScheduled?: (payload: MessageCompositionFooterPayload) => void;
  onReschedule?: (payload: MessageCompositionFooterPayload, sendAt: Date) => void;
  enduserId?: string;
  channelType?: MessageCompositionFooterChannelType;
  editorVariant?: "textarea" | "richtext";
  isEditingScheduled?: boolean;
  sendAtInPast?: boolean;
  initDate?: Date;
  scheduleDialogKey?: string;
  canSend: boolean;
  isUpdatePending?: boolean;
  initialMessage?: string;
  initialAttachments?: ChatAttachment[];
  placeholder?: string;
  autoFocus?: boolean;
  submitOnEnter?: boolean;
  leadingActions?: ReactNode;
  trailingActions?: ReactNode;
  trackingIds?: {
    schedule?: string;
    reschedule?: string;
  };
  onMessageChange?: (value: string) => void;
  ref?: RefObject<HTMLTextAreaElement | null>;
}

const channelFilterByType: Record<MessageCompositionFooterChannelType, "Chat" | "SMS" | "Email" | undefined> = {
  chat: "Chat",
  sms: "SMS",
  email: "Email",
  zendesk: undefined,
};

export const MessageCompositionFooter = ({
  onSubmit,
  onSchedule,
  onSendNow,
  onSaveScheduled,
  onReschedule,
  enduserId,
  channelType = "chat",
  editorVariant = "textarea",
  isEditingScheduled = false,
  sendAtInPast = false,
  initDate,
  scheduleDialogKey,
  canSend,
  isUpdatePending = false,
  initialMessage,
  initialAttachments,
  placeholder = i18n.t("Type your message...", { ns: "messaging" }),
  autoFocus = true,
  submitOnEnter = true,
  leadingActions,
  trailingActions,
  trackingIds,
  onMessageChange,
  ref,
}: MessageCompositionFooterProps) => {
  const { t } = useTranslation("messaging");
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isSelectingTemplate, setIsSelectingTemplate] = useState(false);
  const { currentUser } = useAuth();

  const isRichText = editorVariant === "richtext";
  const isTeamChat = channelType === "chat" && !enduserId;
  const channelTypeForFilter = channelFilterByType[channelType];
  const attachChannelType = channelType === "zendesk" ? "email" : channelType;

  const { attachments, files, addExistingFiles, removeAttachment } = useMessagingAttachments({
    enduserId,
    attachments: initialAttachments,
  });

  const buildPayload = (message: string): MessageCompositionFooterPayload => ({
    message,
    attachments,
    files,
  });

  const form = useForm({
    fields: {
      message: {
        type: "textarea",
        initialValue: initialMessage ?? "",
        required: !attachments.length,
      },
    },
    validators: {
      message: (value) => {
        if (!isRichText && value.length > 1200) return t("Message cannot exceed 1200 characters");
        if (value.length === 0 && !attachments.length) return t("Message cannot be empty");
        return undefined;
      },
    },
    validateOn: "change",
    onSubmit: (values) => {
      onSubmit(buildPayload(values.message));
    },
  });

  const messageFieldProps = form.getCustomFieldProps("message");
  const actionRowClassName = `flex justify-end gap-2 ${isRichText ? "items-center" : "items-end"}`;

  const handleAttachContent = (records: TellescopeModelFromName<"managed_content_records">[]) => {
    const tokens = records
      .map((record) => formatContentInlineTemplateString({ id: record.id, displayName: record.title }))
      .join(" ");
    if (!tokens) return;

    const current = form.values.message;
    form.setFieldValue("message", current ? `${current} ${tokens}` : tokens);
  };

  return (
    <form
      className={`border-t p-4 flex-shrink-0${isEditingScheduled ? " bg-amber-50 dark:bg-amber-950/20" : ""}`}
      {...form.getFormProps()}
    >
      <div className="space-y-3">
        {isRichText ? (
          <RichTextEditor
            content={messageFieldProps.value}
            onChange={messageFieldProps.onValueChange}
            onBlur={messageFieldProps.onBlur}
            placeholder={placeholder}
            className="w-full"
          />
        ) : (
          <Textarea
            placeholder={placeholder}
            {...form.getFieldProps("message")}
            ref={ref}
            className="flex-1 min-h-[80px] resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && submitOnEnter) {
                form.getFormProps().onSubmit(e);
              }
            }}
            onChange={(e) => {
              form.getFieldProps("message").onChange(e);
              onMessageChange?.(e.target.value);
            }}
            autoFocus={autoFocus}
          />
        )}
        <div className={actionRowClassName}>
          {leadingActions}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment) => (
                <InboxAttachmentPreview
                  key={attachment.secureName}
                  attachment={attachment}
                  onRemove={() => removeAttachment(attachment.secureName)}
                />
              ))}
            </div>
          )}
          {channelTypeForFilter && (
            <Dialog open={isSelectingTemplate} onOpenChange={setIsSelectingTemplate}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" hoverLabel={t("Insert Template")}>
                  <FileText />
                </Button>
              </DialogTrigger>
              <InboxTemplateSelectorDialogContents
                onSelectTemplate={(template) => {
                  form.setFieldValue("message", isRichText ? template.html || template.message : template.message);
                  setIsSelectingTemplate(false);
                }}
                onCancel={() => setIsSelectingTemplate(false)}
                contextFilters={{
                  channels: [channelTypeForFilter],
                  enduserOnly: !isTeamChat,
                  enduserId,
                  userId: currentUser.id,
                }}
                enableQuery={isSelectingTemplate}
              />
            </Dialog>
          )}
          <AttachFileDialog
            variant="managed-content-records"
            enduserId={enduserId}
            onAttachContent={handleAttachContent}
          >
            <Button variant="ghost" size="icon" hoverLabel={t("Attach Content")} type="button">
              <File />
            </Button>
          </AttachFileDialog>
          <AttachFileDialog
            enduserId={enduserId}
            channelType={attachChannelType}
            onUploadNew={addExistingFiles}
            onAttachExisting={addExistingFiles}
            selectedSecureNames={attachments.map((a) => a.secureName)}
          >
            <Button variant="ghost" size="icon" hoverLabel={t("Attach File")} type="button">
              <Paperclip />
            </Button>
          </AttachFileDialog>
          {trailingActions}
          {isEditingScheduled ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canSend || isUpdatePending || !form.isValid}
                onClick={() => onSendNow?.(buildPayload(form.values.message))}
              >
                {t("Send now")}
              </Button>
              <ScheduleMessageDialog
                open={isRescheduleDialogOpen}
                onOpenChange={setIsRescheduleDialogOpen}
                initDate={initDate}
                onSchedule={(sendAt) => onReschedule?.(buildPayload(form.values.message), sendAt)}
              >
                <Button
                  type="button"
                  variant={sendAtInPast ? "default" : "outline"}
                  size="sm"
                  disabled={!canSend || isUpdatePending || !form.isValid}
                  data-trackingid={trackingIds?.reschedule}
                >
                  {t("Reschedule")}
                </Button>
              </ScheduleMessageDialog>
              {!sendAtInPast && (
                <Button
                  type="button"
                  size="sm"
                  disabled={!canSend || isUpdatePending || !form.isValid}
                  onClick={() => onSaveScheduled?.(buildPayload(form.values.message))}
                >
                  {t("Save")}
                </Button>
              )}
            </>
          ) : (
            <>
              {onSchedule && (
                <ScheduleMessageDialog
                  key={scheduleDialogKey}
                  initDate={initDate}
                  onSchedule={(sendAt) => onSchedule(buildPayload(form.values.message), sendAt)}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canSend || !form.isValid}
                    type="button"
                    data-trackingid={trackingIds?.schedule}
                  >
                    <Clock />
                  </Button>
                </ScheduleMessageDialog>
              )}
              <Button type="submit" disabled={!canSend || !form.isValid} size="sm">
                {t("Send")}
              </Button>
            </>
          )}
        </div>
      </div>
    </form>
  );
};
